import * as React from "react";

import { isMainwebWarmupSettled, whenMainwebWarm } from "@/helpers/warmup";

/**
 * Whether pf-mainweb has answered the scale-to-zero wake ping.
 *
 * The app shell holds its spinner on this so an authenticated user landing on a
 * cold backend never sees Drive's skeleton standing in for a list that is still
 * ~10-15s away — see RootLayout.
 *
 * `enabled` must stay false until the caller is a confirmed authenticated user.
 * The ping wakes a tenant pod, so it carries the same rule as public/warmup.js's
 * returning-user cookie check: an anonymous visitor on a protected URL mounts
 * the shell before being bounced to /login and must not be able to scale a
 * sleeping tenant up from zero.
 *
 * Seeded from the module-level latch so a remount after the ping has already
 * settled renders warm on the first paint, with no spinner flash.
 */
export const useMainwebWarm = (enabled: boolean): boolean => {
  const [isWarm, setIsWarm] = React.useState(isMainwebWarmupSettled);

  React.useEffect(() => {
    if (!enabled || isWarm) return;
    let cancelled = false;
    void whenMainwebWarm().then(() => {
      if (!cancelled) setIsWarm(true);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, isWarm]);

  return isWarm;
};
